import React from "react";
import { Link } from "react-router-dom";
import WikiContents from "./WikiContents";
import "./Wiki.css";

const sources = [
  {
    id: "r1",
    href: "/pdf/1978-Chimpanzee-Language-Research.pdf",
    label: "Rumbaugh & Savage-Rumbaugh, 1978",
    title: "Chimpanzee language research: Status and potential",
  },
  {
    id: "r2",
    href: "/pdf/1978-Symbolic-Communication-Chimps.pdf",
    label: "Savage-Rumbaugh, Rumbaugh & Boysen, 1978",
    title: "Symbolic communication between two chimpanzees",
  },
  {
    id: "r3",
    href: "/pdf/1980-Reference-Linguistic-Essential.pdf",
    label: "Savage-Rumbaugh et al., 1980",
    title: "Reference: The Linguistic Essential",
  },
  {
    id: "r4",
    href: "/pdf/1986-Spontaneous-Symbol-Acquisition.pdf",
    label: "Savage-Rumbaugh et al., 1986",
    title: "Spontaneous symbol acquisition and communicative use by pygmy chimpanzees",
  },
  {
    id: "r5",
    href: "/pdf/1993-Language-Comprehension-Ape.pdf",
    label: "Savage-Rumbaugh et al., 1993",
    title: "Language comprehension in ape and child",
  },
  {
    id: "r6",
    href: "/pdf/1993-Pan-Tool-Maker.pdf",
    label: "Schick et al., 1993",
    title: "Continuing investigations into the stone tool-making and tool-using capabilities of a bonobo",
  },
  {
    id: "r7",
    href: "/pdf/2000-Observational-Word-Learning-Bonobos.pdf",
    label: "Lyn, 2000",
    title: "Observational word learning in bonobos",
  },
  {
    id: "r8",
    href: "/pdf/2005-Culture-Prefigures-Cognition.pdf",
    label: "Savage-Rumbaugh et al., 2005",
    title: "Culture prefigures cognition in Pan/Homo bonobos",
  },
  {
    id: "r9",
    href: "/pdf/2010-Semiotic-Combinations-Pan.pdf",
    label: "Lyn et al., 2010",
    title: "Semiotic combinations in Pan",
  },
  {
    id: "r10",
    href: "/pdf/2012-Stone-Tool-Production.pdf",
    label: "Roffman et al., 2012",
    title: "Stone tool production and utilization by bonobo-chimpanzees",
  },
  {
    id: "r11",
    href: "/pdf/2014-Gestural-Symbolic-Development.pdf",
    label: "Gillespie-Lynch et al., 2014",
    title: "Gestural and symbolic development among apes and humans",
  },
  {
    id: "r12",
    href: "/pdf/2019-Language-Growth-Hands-Mouths.pdf",
    label: "Roffman et al., 2019",
    title: "Language growth in hands and mouths",
  },
  {
    id: "r13",
    href: "/pdf/2019-Pan Survival.pdf",
    label: "Roffman et al., 2019",
    title: "Pan survival in the 21st century",
  },
  {
    id: "r14",
    label: "Bettoni, 2007",
    title: "The Yerkish Language: From Operational Methodology to Chimpanzee Communication",
  },
];

const ref = (id) => (
  <sup className="wiki-ref" key={id}>
    <a href={`#${id}`}>{id.replace("r", "")}</a>
  </sup>
);

const refs = (...ids) => ids.map((id) => ref(id));

const sections = [
  ["opening", "Opening Question"],
  ["lana-yerkish", "Lana and Yerkish"],
  ["sherman-austin", "Sherman and Austin"],
  ["reference", "Reference"],
  ["kanzi", "Kanzi and Development"],
  ["culture", "Culture and Tools"],
  ["restoration", "Restoration"],
  ["references", "References"],
];

const Wiki = () => (
  <main className="wiki-page">
    <WikiContents sections={sections} />

    <article className="wiki-article">
      <h1>Research Trajectory</h1>

      <section id="opening">
        <p className="wiki-lead">
          The Bonobo Hope research trajectory is a body of ape-language,
          cognition, and culture research centered on{" "}
          <Link to="/research/lexigrams">lexigrams</Link>,
          symbolic reference, bonobo development, spoken-language comprehension,
          tool-making, gesture, and the ethical status of{" "}
          <Link to="/research/pan">Pan</Link>. Its central question is not whether
          apes can mimic isolated human behaviors, but what happens when
          symbolic systems, social life, and culture are made available to them
          as a shared world.
        </p>
        <p>
          The arc is cumulative. Each phase changes the question left behind by
          the previous one: Lana makes symbol use recordable, Sherman and Austin
          make it social, the reference work asks what symbols can mean, Kanzi
          moves the field into development and comprehension, and the later Pan
          papers connect language to culture, tools, memory, and ethics.
        </p>
      </section>

      <section id="lana-yerkish">
        <h2>From Lana to a Symbolic Environment</h2>
        <p>
          The trajectory begins with{" "}
          <Link to="/research/lana">Lana</Link> and the{" "}
          <Link to="/research/lana#lana-project">
            Language Analog Research Project
          </Link>{" "}
          at <Link to="/research/yerkes">Yerkes</Link>, where a computer-controlled{" "}
          <Link to="/research/yerkish">Yerkish</Link>{" "}
          keyboard made ape symbol use experimentally tractable.{" "}
          <Link to="/research/ernst-von-glasersfeld">Ernst von Glasersfeld</Link>{" "}
          designed Yerkish around abstract{" "}
          <Link to="/research/lexigrams">lexigrams</Link> and a{" "}
          <Link to="/research/ernst-von-glasersfeld#grammar">
            correlational grammar
          </Link>
          , giving researchers a formal symbolic environment rather than a
          loose set of picture keys. {refs("r1", "r14")}
        </p>

        <p>
          That foundation matters because the research was never simply about
          whether an ape could press a button.{" "}
          <Link to="/research/lana">Lana</Link>'s stock
          sentences, naming work, and exchanges with humans raised the central
          methodological problem: when does a symbol function only as a trained
          response, and when does it begin to behave like a word, a reference,
          or an element in a rule-governed expression? That question set the
          stage for the next decisive step: asking whether symbols could carry
          information between apes, not only between an ape and a human.
          {refs("r1", "r3")}
        </p>
      </section>

      <section id="sherman-austin">
        <h2>Sherman and Austin Make Symbols Social</h2>
        <p>
          The <Link to="/research/sherman-and-austin">Sherman and Austin</Link>{" "}
          studies are a major bridge in the trajectory.
          Their work moved the keyboard from individual training into
          interanimal communication. In the food-container tasks, one chimpanzee
          had information the other lacked, and the uninformed animal's choice
          depended on the informed animal's{" "}
          <Link to="/research/lexigrams">lexigram</Link>{" "}
          message. That design made the language question social: could a symbol
          be used to alter what another ape knew and did?
          {refs("r1", "r2")}
        </p>

        <p>
          This matters because it helped separate symbol use from simple
          vending-machine requests. <Link to="/research/lana">Lana</Link> had shown that a chimpanzee could work
          inside a controlled <Link to="/research/yerkish">Yerkish</Link> environment;{" "}
          <Link to="/research/sherman-and-austin">Sherman and Austin</Link> showed why
          the system mattered for communication between minds. Their experiments
          prepared the way for later questions about reference, comprehension,
          shared attention, and the developmental environment that became
          central in the bonobo work. {refs("r2", "r3", "r4")}
        </p>
      </section>

      <section id="reference">
        <h2>Symbols Become Reference</h2>
        <p>
          Once the keyboard made symbolic acts visible and repeatable, the
          research could ask a more difficult question: whether a{" "}
          <Link to="/research/lexigrams">lexigram</Link>{" "}
          could stand for an object, action, relation, or event beyond the
          immediate reward structure. Reference becomes the hinge of the archive. The work
          moves from stock sentences and naming tasks toward the possibility
          that symbols can carry meaning across contexts, partners, and sensory
          modes. {refs("r1", "r3", "r5")}
        </p>
        <p>
          This is why the archive repeatedly returns to naming, requesting,
          comprehension, and context. A request can be trained as an action that
          produces a result, but reference asks whether the symbol can carry
          meaning when the referent is absent, hidden, newly encountered, or
          relevant to another individual.
          {refs("r3", "r5", "r7")}
        </p>
      </section>

      <section id="kanzi">
        <h2>Kanzi Changes the Scale</h2>
        <p>
          The trajectory then shifts from controlled symbol systems toward
          developmental life with <Link to="/research/bonobos">bonobos</Link>.{" "}
          <Link to="/research/kanzi">Kanzi</Link>'s importance lies in that
          change of scale. His language abilities were studied not only as
          keyboard output, but as receptive understanding, spoken-language
          comprehension, observation, shared attention, and participation in a
          language-rich social world. The question becomes less "can an ape be
          trained to produce a symbol?" and more "what can{" "}
          <Link to="/research/pan">Pan</Link> learn when symbols, speech, gesture,
          people, artifacts, and daily routines form a culture around them?"
          {refs("r4", "r5", "r7", "r8")}
        </p>
        <p>
          The later bonobo papers make this developmental turn explicit. Kanzi
          did not simply add another data point to a training literature; his
          case reframed the research around observation, enculturation,
          receptive language, and the possibility that culture can prefigure
          cognition rather than merely express it after the fact.
          {refs("r5", "r7", "r8")}
        </p>
      </section>

      <section id="culture">
        <h2>Language Widens Into Culture</h2>
        <p>
          From there, the papers widen again. Tool-making and stone flaking bring
          language research into material culture, showing that symbolic life
          cannot be cleanly separated from embodied skill, imitation, planning,
          and learned practice. Later work on gesture, semiotic combinations,
          hands, mouths, and pointing treats communication as multimodal rather
          than keyboard-bound.{" "}
          <Link to="/research/lexigrams">Lexigrams</Link>{" "}
          remain central, but they sit inside a
          larger ecology of bodies, objects, attention, and relationships.
          {refs("r6", "r9", "r10", "r11", "r12")}
        </p>
        <p>
          In this wider frame, language is not a detachable module. It is tied
          to what apes do with objects, how they learn from others, how they
          coordinate attention, and how symbolic environments change the kinds
          of acts that can be remembered, requested, demonstrated, or taught.
          {refs("r8", "r10", "r12")}
        </p>
      </section>

      <section id="restoration">
        <h2>The Trajectory Points Forward</h2>
        <p>
          The current research problem is therefore not the preservation of a
          static legacy. It is the restoration of the conditions that made the
          work possible: a symbolic and cultural environment where{" "}
          <Link to="/research/bonobos">bonobos</Link> and chimpanzees can continue to participate in language, research, social
          learning, and public understanding. The later ethical argument follows
          from the science itself: if <Link to="/research/pan">Pan</Link> lives within culture, communication,
          and social meaning, then access, continuity, dignity, and research
          conditions are part of the trajectory rather than an afterword.
          {refs("r8", "r12", "r13")}
        </p>
      </section>

      <section id="references">
        <div className="wiki-reference-head">
          <h2>References</h2>
          <Link to="/SueBiblio">Full bibliography</Link>
        </div>
        <ol className="wiki-references">
          {sources.map((source) => (
            <li id={source.id} key={source.id}>
              {source.href ? (
                <a href={source.href} target="_blank" rel="noreferrer">
                  {source.label}. <em>{source.title}</em>.
                </a>
              ) : (
                <>
                  {source.label}. <em>{source.title}</em>.
                </>
              )}
            </li>
          ))}
        </ol>
      </section>
    </article>
  </main>
);

export default Wiki;
